import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import jwt from "jwt-decode";
import user_service from "../service/user_service";
import Loader from "../../Pages/Loader/Loader.js";
import Toaster from "../../Pages/Toaster/Toaster.js";

const STATUS_OPTIONS = [
  { value: "pending", label: "Pending (new + edit requests)" },
  { value: "approved", label: "Approved" },
  { value: "unapproved", label: "Unapproved" },
];

function safeDecodeAuth() {
  const token = localStorage.getItem("auth");
  if (!token) return null;
  try {
    return jwt(token);
  } catch {
    return null;
  }
}

function formatDate(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString();
}

function formatDateTime(value) {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString();
}

function prettyValue(v) {
  if (v == null) return "—";
  if (typeof v === "string") return v.trim() ? v : "—";
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  // Common vendor-ad complex fields: render them more cleanly than raw JSON.
  if (Array.isArray(v)) {
    // Areas served (zipcodes) is usually a string array.
    if (v.every((x) => typeof x === "string" || typeof x === "number")) {
      const xs = v.map((x) => String(x).trim()).filter(Boolean);
      return xs.length ? xs.join(", ") : "—";
    }
    // Social links: [{ type, linkurl }]
    if (
      v.length > 0 &&
      v.every(
        (x) =>
          x &&
          typeof x === "object" &&
          (Object.prototype.hasOwnProperty.call(x, "type") ||
            Object.prototype.hasOwnProperty.call(x, "linkurl"))
      )
    ) {
      const lines = v
        .map((x) => {
          const type = x?.type ? String(x.type).trim() : "";
          const url = x?.linkurl ? String(x.linkurl).trim() : "";
          if (!type && !url) return "";
          if (type && url) return `${type} — ${url}`;
          return type || url;
        })
        .filter(Boolean);
      return lines.length ? lines.join("\n") : "—";
    }
    // Images: [{ images }]
    if (
      v.length > 0 &&
      v.every((x) => x && typeof x === "object" && Object.prototype.hasOwnProperty.call(x, "images"))
    ) {
      const lines = v
        .map((x) => (x?.images ? String(x.images).trim() : ""))
        .filter(Boolean);
      return lines.length ? lines.join("\n") : "—";
    }
  }
  try {
    return JSON.stringify(v, null, 2);
  } catch {
    return String(v);
  }
}

function normalizeSocialLinks(value) {
  const rows = Array.isArray(value) ? value : [];
  return rows
    .map((x) => {
      const type = x?.type ? String(x.type).trim() : "";
      const url = x?.linkurl ? String(x.linkurl).trim() : "";
      return { type, url };
    })
    .filter((x) => x.type || x.url);
}

function socialLinksMap(value) {
  const out = new Map();
  for (const row of normalizeSocialLinks(value)) {
    const key = row.type || "(no type)";
    if (!out.has(key)) out.set(key, []);
    out.get(key).push(row.url);
  }
  // flatten: if multiple URLs per type, join with " | "
  const flattened = new Map();
  for (const [k, urls] of out.entries()) {
    const u = (urls || []).map((x) => String(x || "").trim()).filter(Boolean);
    flattened.set(k, u.length ? u.join(" | ") : "");
  }
  return flattened;
}

function SocialLinksDiff({ before, after }) {
  const b = socialLinksMap(before);
  const a = socialLinksMap(after);
  const keys = Array.from(new Set([...b.keys(), ...a.keys()])).sort((x, y) =>
    String(x).localeCompare(String(y))
  );

  if (keys.length === 0) {
    return (
      <div className="alert alert-light border small mb-0">
        No social links provided.
      </div>
    );
  }

  return (
    <div className="border rounded overflow-hidden">
      <div className="row g-0 bg-light border-bottom">
        <div className="col-4 p-2 small text-muted fw-semibold">Channel</div>
        <div className="col-4 p-2 small text-muted fw-semibold">Current (live)</div>
        <div className="col-4 p-2 small text-muted fw-semibold">Proposed</div>
      </div>
      {keys.map((k, idx) => {
        const beforeUrl = b.get(k) || "";
        const afterUrl = a.get(k) || "";
        const changed = toComparableString(beforeUrl) !== toComparableString(afterUrl);
        const status = !beforeUrl && afterUrl ? "Added" : beforeUrl && !afterUrl ? "Removed" : "Changed";
        return (
          <div key={k} className={`row g-0 ${idx ? "border-top" : ""}`}>
            <div className="col-4 p-2 small d-flex align-items-center gap-2">
              <span className="fw-semibold">{k}</span>
              {changed ? (
                <span className="badge rounded-pill bg-warning text-dark">{status}</span>
              ) : (
                <span className="badge rounded-pill bg-light text-muted border">
                  Unchanged
                </span>
              )}
            </div>
            <div className="col-4 p-2 small text-break">
              {beforeUrl ? (
                <a href={beforeUrl} target="_blank" rel="noopener noreferrer">
                  {beforeUrl}
                </a>
              ) : (
                <span className="text-muted fst-italic">—</span>
              )}
            </div>
            <div className="col-4 p-2 small text-break">
              {afterUrl ? (
                <a href={afterUrl} target="_blank" rel="noopener noreferrer">
                  {afterUrl}
                </a>
              ) : (
                <span className="text-muted fst-italic">—</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function toComparableString(v) {
  if (v == null) return "";
  if (typeof v === "string") return v.trim();
  if (typeof v === "number" || typeof v === "boolean") return String(v);
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

const EDIT_FIELD_LABELS = {
  productname: "Product name",
  productblurb: "Product blurb",
  description: "Short description",
  linkurl: "Website URL",
  email: "Email",
  contactName: "Contact name",
  phone: "Phone",
  product_fullDetails: "Full details (rich text)",
  image: "Images",
  address: "Street address",
  address2: "Street address 2",
  city: "City",
  state: "State",
  zip: "Zip",
  social_links: "Social links",
  zipcode: "Areas served",
  companyName: "Company name",
};

function computePendingEdits(detailItem) {
  const pending = detailItem?.vendorAdPendingChanges;
  if (!pending || typeof pending !== "object") return [];
  const keys = Object.keys(pending);
  const out = [];
  for (const key of keys) {
    const before = detailItem?.[key];
    const after = pending?.[key];
    if (toComparableString(before) === toComparableString(after)) continue;
    out.push({
      key,
      label: EDIT_FIELD_LABELS[key] || key,
      before,
      after,
      isLong: key === "product_fullDetails" || typeof after === "object",
    });
  }
  return out;
}

/** Vendor listing may reference the vendor contact on `vendorContactId` or `agentId`. */
function vendorContactIdFromListing(item) {
  if (!item) return null;
  const raw = item.vendorContactId ?? item.agentId;
  if (raw == null || raw === "") return null;
  if (typeof raw === "string") return raw;
  if (typeof raw === "object" && raw._id != null) return String(raw._id);
  return String(raw);
}

function PaymentBadge({ status }) {
  const s = String(status || "").toLowerCase();
  if (s === "paid") {
    return <span className="badge rounded-pill bg-success">Paid</span>;
  }
  if (s === "pending") {
    return (
      <span className="badge rounded-pill bg-warning text-dark">Payment pending</span>
    );
  }
  if (s === "refund_pending") {
    return (
      <span className="badge rounded-pill bg-warning text-dark">
        Refund pending
      </span>
    );
  }
  if (s === "refund_failed") {
    return <span className="badge rounded-pill bg-danger">Refund failed</span>;
  }
  if (s === "refunded" || s === "failed" || s === "expired") {
    return <span className="badge rounded-pill bg-secondary">{status}</span>;
  }
  return (
    <span className="badge rounded-pill bg-light text-dark border">
      {status || "—"}
    </span>
  );
}

function PackageBadge({ months }) {
  if (months == null || months === "") return null;
  return (
    <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">
      {months} mo
    </span>
  );
}

/** Renders a label + value row in the detail modal. */
function DetailRow({ label, value, multiline }) {
  const text = value == null || value === "" ? "—" : String(value);
  return (
    <div className="row py-2 border-bottom border-light">
      <div className="col-sm-4 text-muted small">{label}</div>
      <div
        className={`col-sm-8 small ${multiline ? "text-break" : ""}`}
        style={multiline ? { whiteSpace: "pre-wrap" } : undefined}
      >
        {text}
      </div>
    </div>
  );
}

const AdminVendorAds = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState({ isActive: null });
  const { isActive } = loader;
  const [toaster, setToaster] = useState({
    types: null,
    isShow: null,
    toasterBody: "",
    message: "",
  });
  const { types, isShow, toasterBody, message } = toaster;

  const decoded = useMemo(() => safeDecodeAuth(), []);
  const isAdmin =
    decoded?.contactType === "admin" || decoded?.contactType === "staff";
  const isStrictAdmin = decoded?.contactType === "admin";

  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [detailItem, setDetailItem] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailVendorSignatureUrl, setDetailVendorSignatureUrl] = useState(null);
  const [detailActionLoading, setDetailActionLoading] = useState(false);

  const openDetails = async (row) => {
    setDetailItem(row);
    setDetailVendorSignatureUrl(null);
    setDetailLoading(true);
    try {
      let full = null;
      try {
        const resp = await user_service.productItemsGetId(row._id);
        full = resp?.data;
        if (full) setDetailItem(full);
      } catch {
        /* keep list row */
      }
      const listing = full || row;
      const contactId = vendorContactIdFromListing(listing);
      if (contactId) {
        try {
          const cResp = await user_service.contactGetById(contactId);
          const sig = cResp?.data?.onboard_signature;
          if (sig && String(sig).trim()) {
            setDetailVendorSignatureUrl(String(sig).trim());
          }
        } catch {
          /* signature is optional if contact fetch fails */
        }
      }
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetails = () => {
    setDetailItem(null);
    setDetailLoading(false);
    setDetailVendorSignatureUrl(null);
    setDetailActionLoading(false);
  };

  const load = async (selectedStatus = status) => {
    setLoader({ isActive: true });
    try {
      const resp = await user_service.vendorAdsAdminQueue(selectedStatus);
      const data = resp?.data?.data || [];
      setItems(data);
      setLoader({ isActive: false });
    } catch (e) {
      setLoader({ isActive: false });
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: "Unable to load vendor ads queue.",
        message: e?.message || "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2500);
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    load(status);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status]);

  const approve = async (id) => {
    setLoader({ isActive: true });
    try {
      await user_service.vendorAdsAdminApprove(id);
      setLoader({ isActive: false });
      setToaster({
        types: "success",
        isShow: true,
        toasterBody: "Vendor ad approved.",
        message: "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2000);
      await load(status);
    } catch (e) {
      setLoader({ isActive: false });
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: "Approval failed.",
        message: e?.response?.data?.message || e?.message || "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2500);
    }
  };

  const approveEdits = async (id) => {
    setDetailActionLoading(true);
    try {
      await user_service.vendorAdsAdminApproveEdit(id);
      setDetailActionLoading(false);
      setToaster({
        types: "success",
        isShow: true,
        toasterBody: "Vendor ad changes approved.",
        message: "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2000);
      await load(status);
      closeDetails();
    } catch (e) {
      setDetailActionLoading(false);
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: "Approve changes failed.",
        message: e?.response?.data?.message || e?.message || "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2500);
    }
  };

  const reject = async (id) => {
    setLoader({ isActive: true });
    try {
      await user_service.vendorAdsAdminReject(id);
      setLoader({ isActive: false });
      setToaster({
        types: "success",
        isShow: true,
        toasterBody: "Vendor ad rejected.",
        message: "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2000);
      await load(status);
    } catch (e) {
      setLoader({ isActive: false });
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: "Rejection failed.",
        message: e?.response?.data?.message || e?.message || "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2500);
    }
  };

  const rejectEdits = async (id) => {
    setDetailActionLoading(true);
    try {
      await user_service.vendorAdsAdminRejectEdit(id);
      setDetailActionLoading(false);
      setToaster({
        types: "success",
        isShow: true,
        toasterBody: "Vendor ad changes declined.",
        message: "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2000);
      await load(status);
      closeDetails();
    } catch (e) {
      setDetailActionLoading(false);
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: "Decline changes failed.",
        message: e?.response?.data?.message || e?.message || "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2500);
    }
  };

  const retryRefund = async (id) => {
    setLoader({ isActive: true });
    try {
      await user_service.vendorAdsAdminRetryRefund(id);
      setLoader({ isActive: false });
      setToaster({
        types: "success",
        isShow: true,
        toasterBody: "Refund retry started.",
        message: "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2000);
      await load(status);
    } catch (e) {
      setLoader({ isActive: false });
      setToaster({
        types: "error",
        isShow: true,
        toasterBody: "Refund retry failed.",
        message: e?.response?.data?.message || e?.message || "",
      });
      setTimeout(() => setToaster((t) => ({ ...t, isShow: false })), 2500);
    }
  };

  if (!isAdmin) {
    return (
      <div className="bg-secondary float-left w-100 pt-4 mb-4">
        <main className="page-wrapper profile_page_wrap">
          <div className="content-overlay">
            <div className="container">
              <div className="alert alert-warning mt-4">
                You do not have access to this page.
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="bg-secondary float-left w-100 pt-4 mb-4">
      <Loader isActive={isActive} />
      {isShow && (
        <Toaster
          types={types}
          isShow={isShow}
          toasterBody={toasterBody}
          message={message}
        />
      )}
      <main className="page-wrapper profile_page_wrap">
        <div className="content-overlay">
          <div className="container">
            <div className="float-left w-100 mb-4">
              <h3 className="text-white mb-1">Vendor Ads Approval</h3>
              <p className="text-white-50 small mb-0 mb-3">
                <strong>Pending</strong> lists only vendor ads whose payment has
                completed (ready for your review). Unpaid drafts stay in the
                vendor&apos;s account until they pay. Open{" "}
                <strong>View details</strong> to review and approve.
              </p>
              <div className="d-flex flex-wrap align-items-center gap-2">
                <select
                  className="form-select"
                  style={{ maxWidth: 200 }}
                  value={status}
                  onChange={(e) => setStatus(e.target.value)}
                >
                  {STATUS_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {/* {isStrictAdmin ? (
                  <button
                    className="btn btn-light"
                    type="button"
                    onClick={() => navigate("/add-vendor")}
                  >
                    <i className="fa fa-plus me-1" aria-hidden="true" />
                    Create New Vendor
                  </button>
                ) : null} */}
                <button
                  className="btn btn-outline-light"
                  type="button"
                  onClick={() => load(status)}
                >
                  <i className="fa fa-refresh me-1" aria-hidden="true" />
                  Refresh
                </button>
              </div>
            </div>

            {items?.length ? (
              <div className="d-flex flex-column gap-3">
                {items.map((it) => (
                  (() => {
                    const editPending =
                      String(it.vendorAdEditStatus || "").toLowerCase() === "pending" &&
                      String(it.vendorAdStatus || "").toLowerCase() === "approved";
                    return (
                  <div
                    key={it._id}
                    className="card border-0 shadow-sm overflow-hidden"
                  >
                    <div className="card-body p-4">
                      <div className="min-w-0">
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                          <h5 className="mb-0 text-truncate" title={it.productname}>
                            {it.productname || "Untitled listing"}
                          </h5>
                          <PackageBadge months={it.vendorAdPackageMonths} />
                          <PaymentBadge status={it.vendorAdPaymentStatus} />
                        </div>
                        <p className="text-muted small mb-2">
                          <span className="fw-semibold text-body">Company: </span>
                          {it.companyName ? (
                            it.companyName
                          ) : (
                            <span className="fst-italic text-muted">
                              Not provided
                            </span>
                          )}
                        </p>
                        <div className="small text-muted mb-2">
                          {(it.email || it.phone || it.contactName) && (
                            <span className="me-2">
                              {it.contactName ? (
                                <span className="me-2">{it.contactName}</span>
                              ) : null}
                              {it.email ? (
                                <a href={`mailto:${it.email}`}>{it.email}</a>
                              ) : null}
                              {it.email && it.phone ? " · " : null}
                              {it.phone ? (
                                <a href={`tel:${it.phone}`}>{it.phone}</a>
                              ) : null}
                            </span>
                          )}
                        </div>
                        <div className="d-flex flex-wrap gap-3 small text-muted">
                          <span>
                            <i className="fa fa-calendar-o me-1" aria-hidden="true" />
                            {editPending ? (
                              <>
                                Edit requested:{" "}
                                <strong className="text-body">
                                  {formatDate(it.vendorAdEditRequestedAt) || "—"}
                                </strong>
                              </>
                            ) : (
                              <>
                                Requested:{" "}
                                <strong className="text-body">
                                  {formatDate(it.vendorAdApprovalRequestedAt) || "—"}
                                </strong>
                              </>
                            )}
                          </span>
                          <span>
                            <i className="fa fa-clock-o me-1" aria-hidden="true" />
                            Expires:{" "}
                            <strong className="text-body">
                              {it.vendorAdExpiresAt
                                ? formatDate(it.vendorAdExpiresAt)
                                : "—"}
                            </strong>
                            {!it.vendorAdExpiresAt && (
                              <span className="text-muted ms-1">
                                (after approval)
                              </span>
                            )}
                          </span>
                        </div>
                      </div>

                      <div className="d-flex flex-wrap align-items-center gap-2 mt-4 pt-3 border-top border-light">
                        <button
                          type="button"
                          className="btn btn-outline-primary btn-sm"
                          onClick={() => openDetails(it)}
                        >
                          <i className="fa fa-eye me-1" aria-hidden="true" />
                          {editPending ? "Review changes" : "View details"}
                        </button>
                        {status === "pending" ? (
                          !editPending ? (
                            <div
                              className="btn-group ms-auto"
                              role="group"
                              aria-label="Approve or reject listing"
                            >
                              <button
                                type="button"
                                className="btn btn-success btn-sm px-3"
                                onClick={() => approve(it._id)}
                              >
                                <i className="fa fa-check me-1" aria-hidden="true" />
                                Approve
                              </button>
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm px-3"
                                onClick={() => reject(it._id)}
                              >
                                <i className="fa fa-times me-1" aria-hidden="true" />
                                Reject
                              </button>
                            </div>
                          ) : (
                            <div className="ms-auto small text-muted">
                              Open <strong>Review changes</strong> to approve/decline.
                            </div>
                          )
                        ) : (
                          <div className="ms-auto d-flex flex-wrap gap-2 align-items-center">
                            {String(it.vendorAdPaymentStatus || "").toLowerCase() ===
                              "refund_failed" && (
                              <button
                                type="button"
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => retryRefund(it._id)}
                                title="Retry Stripe refund"
                              >
                                <i
                                  className="fa fa-undo me-1"
                                  aria-hidden="true"
                                />
                                Retry refund
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                    );
                  })()
                ))}
              </div>
            ) : (
              <div className="card border-0 shadow-sm">
                <div className="card-body text-center py-5 text-muted">
                  <i
                    className="fa fa-inbox fa-3x mb-3 d-block opacity-50"
                    aria-hidden="true"
                  />
                  No vendor ads in this queue.
                </div>
              </div>
            )}

            {detailItem && (
              <div
                className="modal fade show d-block"
                tabIndex={-1}
                role="dialog"
                aria-modal="true"
                aria-labelledby="vendor-ad-detail-title"
                style={{ backgroundColor: "rgba(33,37,41,0.55)" }}
                onClick={(e) => {
                  if (e.target === e.currentTarget) closeDetails();
                }}
              >
                <div className="modal-dialog modal-lg modal-dialog-scrollable modal-dialog-centered">
                  <div className="modal-content border-0 shadow">
                    <div className="modal-header bg-light border-bottom">
                      <div>
                        <h5 className="modal-title mb-0" id="vendor-ad-detail-title">
                          Vendor ad details
                        </h5>
                        <div className="small text-muted mt-1">
                          {detailItem.productname || "Listing"}{" "}
                          {detailItem.companyName
                            ? `· ${detailItem.companyName}`
                            : ""}
                        </div>
                      </div>
                      <button
                        type="button"
                        className="btn-close"
                        aria-label="Close"
                        onClick={closeDetails}
                      />
                    </div>
                    <div className="modal-body">
                      {detailLoading && (
                        <div className="alert alert-light border small mb-3">
                          <i className="fa fa-spinner fa-spin me-2" aria-hidden="true" />
                          Loading latest from server…
                        </div>
                      )}
                      <h6 className="text-uppercase text-muted small mb-2 fw-bold">
                        Company & contact
                      </h6>
                      <DetailRow label="Company name" value={detailItem.companyName} />
                      <DetailRow label="Owner / contact name" value={detailItem.contactName} />
                      <DetailRow label="Email" value={detailItem.email} />
                      <DetailRow label="Phone" value={detailItem.phone} />
                      <DetailRow label="Website URL" value={detailItem.linkurl} />
                      <DetailRow label="Office" value={detailItem.office_name} />
                      <h6 className="text-uppercase text-muted small mt-3 mb-2 fw-bold">
                        Product & category
                      </h6>
                      <DetailRow label="Product name" value={detailItem.productname} />
                      <DetailRow
                        label="Category"
                        value={detailItem.category}
                        multiline
                      />
                      <DetailRow
                        label="Product blurb"
                        value={detailItem.productblurb}
                        multiline
                      />
                      <DetailRow
                        label="Short description"
                        value={detailItem.description}
                        multiline
                      />
                      <DetailRow label="Ad type" value={detailItem.adType} />

                      <h6 className="text-uppercase text-muted small mt-3 mb-2 fw-bold">
                        Vendor advertising
                      </h6>
                      <DetailRow
                        label="Package (months)"
                        value={
                          detailItem.vendorAdPackageMonths != null
                            ? String(detailItem.vendorAdPackageMonths)
                            : ""
                        }
                      />
                      <DetailRow label="Status" value={detailItem.vendorAdStatus} />
                      <DetailRow
                        label="Edit request status"
                        value={detailItem.vendorAdEditStatus}
                      />
                      <DetailRow label="Payment status" value={detailItem.vendorAdPaymentStatus} />
                      {(() => {
                        const editPending =
                          String(detailItem.vendorAdEditStatus || "").toLowerCase() ===
                            "pending" &&
                          String(detailItem.vendorAdStatus || "").toLowerCase() === "approved" &&
                          detailItem.vendorAdPendingChanges;
                        if (!editPending) return null;
                        const changes = computePendingEdits(detailItem);
                        return (
                          <div className="mt-3">
                            <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-2">
                              <div>
                                <h6 className="text-uppercase text-muted small mb-0 fw-bold">
                                  Proposed changes
                                </h6>
                                <div className="small text-muted mt-1">
                                  Review what will change on the live ad before approving.
                                </div>
                              </div>
                              <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-25">
                                {changes.length} change{changes.length === 1 ? "" : "s"}
                              </span>
                            </div>

                            {changes.length === 0 ? (
                              <div className="alert alert-light border small mb-0">
                                No field-level differences detected.
                              </div>
                            ) : (
                              <div className="border rounded overflow-hidden">
                                {changes.map((c, idx) => (
                                  <div
                                    key={c.key}
                                    className={`p-3 ${idx ? "border-top" : ""}`}
                                    style={{ background: "#fff" }}
                                  >
                                    <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                                      <div className="fw-semibold small">{c.label}</div>
                                      <span className="badge rounded-pill bg-warning text-dark">
                                        Changed
                                      </span>
                                    </div>
                                    <div className="row mt-2 g-3">
                                      {c.key === "social_links" ? (
                                        <div className="col-12">
                                          <SocialLinksDiff
                                            before={c.before}
                                            after={c.after}
                                          />
                                        </div>
                                      ) : (
                                        <>
                                          <div className="col-md-6">
                                            <div className="small text-muted mb-1">
                                              Current (live)
                                            </div>
                                            <div className="border rounded p-2 bg-light small">
                                              <pre
                                                className="mb-0"
                                                style={{
                                                  whiteSpace: "pre-wrap",
                                                  maxHeight: c.isLong ? 180 : 120,
                                                  overflow: "auto",
                                                }}
                                              >
                                                {prettyValue(c.before)}
                                              </pre>
                                            </div>
                                          </div>
                                          <div className="col-md-6">
                                            <div className="small text-muted mb-1">
                                              Proposed
                                            </div>
                                            <div className="border rounded p-2 bg-light small">
                                              <pre
                                                className="mb-0"
                                                style={{
                                                  whiteSpace: "pre-wrap",
                                                  maxHeight: c.isLong ? 180 : 120,
                                                  overflow: "auto",
                                                }}
                                              >
                                                {prettyValue(c.after)}
                                              </pre>
                                            </div>
                                          </div>
                                        </>
                                      )}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                      <DetailRow
                        label="Approval requested"
                        value={formatDateTime(detailItem.vendorAdApprovalRequestedAt)}
                      />
                      <DetailRow
                        label="Paid at"
                        value={formatDateTime(detailItem.vendorAdPaidAt)}
                      />
                      <DetailRow
                        label="Expires"
                        value={formatDateTime(detailItem.vendorAdExpiresAt)}
                      />
                      <DetailRow
                        label="Approved at"
                        value={formatDateTime(detailItem.vendorAdApprovedAt)}
                      />

                      <h6 className="text-uppercase text-muted small mt-3 mb-2 fw-bold">
                        Agreement signature
                      </h6>
                      <div className="row py-2 border-bottom border-light">
                        <div className="col-12">
                          {detailVendorSignatureUrl ? (
                            <div className="border rounded bg-white p-2 d-inline-block">
                              <img
                                src={detailVendorSignatureUrl}
                                alt="Vendor agreement signature"
                                className="d-block"
                                style={{
                                  maxWidth: "100%",
                                  maxHeight: 220,
                                  objectFit: "contain",
                                }}
                              />
                            </div>
                          ) : (
                            <p className="text-muted small mb-0 fst-italic">
                              No signature on file for this vendor contact yet.
                            </p>
                          )}
                        </div>
                      </div>

                      {Array.isArray(detailItem.social_links) &&
                        detailItem.social_links.length > 0 && (
                          <>
                            <h6 className="text-uppercase text-muted small mt-3 mb-2 fw-bold">
                              Social links
                            </h6>
                            <ul className="small mb-0">
                              {detailItem.social_links.map((s, i) => (
                                <li key={i}>
                                  {s?.type || "link"}: {s?.linkurl || "—"}
                                </li>
                              ))}
                            </ul>
                          </>
                        )}

                      {detailItem.product_fullDetails ? (
                        <>
                          <h6 className="text-uppercase text-muted small mt-3 mb-2 fw-bold">
                            Full details (rich text)
                          </h6>
                          <div
                            className="border rounded p-3 bg-light small vendor-ad-html-preview"
                            dangerouslySetInnerHTML={{
                              __html: detailItem.product_fullDetails,
                            }}
                          />
                        </>
                      ) : null}

                      {Array.isArray(detailItem.image) && detailItem.image.length > 0 ? (
                        <>
                          <h6 className="text-uppercase text-muted small mt-3 mb-2 fw-bold">
                            Images
                          </h6>
                          <div className="d-flex flex-wrap gap-2">
                            {detailItem.image.map((img, idx) => {
                              const url = img?.images || img?.url || img;
                              if (!url) return null;
                              return (
                                <a
                                  key={idx}
                                  href={url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="d-inline-block"
                                >
                                  <img
                                    src={url}
                                    alt=""
                                    className="img-thumbnail"
                                    style={{
                                      maxWidth: 160,
                                      maxHeight: 160,
                                      objectFit: "cover",
                                    }}
                                  />
                                </a>
                              );
                            })}
                          </div>
                        </>
                      ) : null}
                    </div>
                    <div className="modal-footer bg-light border-top">
                      {String(detailItem.vendorAdEditStatus || "").toLowerCase() ===
                        "pending" &&
                      String(detailItem.vendorAdStatus || "").toLowerCase() === "approved" ? (
                        <div className="me-auto d-flex flex-wrap gap-2">
                          <button
                            type="button"
                            className="btn btn-success"
                            onClick={() => approveEdits(detailItem._id)}
                            disabled={detailActionLoading}
                          >
                            <i className="fa fa-check me-1" aria-hidden="true" />
                            Approve changes
                          </button>
                          <button
                            type="button"
                            className="btn btn-outline-danger"
                            onClick={() => rejectEdits(detailItem._id)}
                            disabled={detailActionLoading}
                          >
                            <i className="fa fa-times me-1" aria-hidden="true" />
                            Decline changes
                          </button>
                        </div>
                      ) : null}
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={closeDetails}
                        disabled={detailActionLoading}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminVendorAds;
